import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  FileText, Upload, Send, Users, Building2, Plus, Search, Filter,
  ChevronRight, ChevronDown, ChevronLeft, MoreHorizontal, Eye, Edit2, Trash2,
  Check, X, Clock, AlertCircle, CheckCircle2, ArrowLeft, Download,
  DollarSign, Calendar, Truck, ClipboardList, HardHat, Award,
  Mail, Phone, ExternalLink, Copy, RefreshCw, ArrowRight, Receipt,
  Loader2, FileUp, Paperclip, SplitSquareHorizontal, Maximize2, Minimize2,
  FolderOpen, Archive, Star, PanelLeftClose, PanelLeft
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { toast } from 'sonner';
import HelpIcon from '@/components/HelpIcon';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status configurations
const RFP_STATUS = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700', icon: FileText },
  sent: { label: 'Sent', color: 'bg-blue-100 text-blue-700', icon: Send },
  responses_received: { label: 'Bids In', color: 'bg-amber-100 text-amber-700', icon: Mail },
  awarded: { label: 'Awarded', color: 'bg-emerald-100 text-emerald-700', icon: Award },
  closed: { label: 'Closed', color: 'bg-gray-100 text-gray-700', icon: X },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700', icon: X },
};

const BID_STATUS = {
  received: { label: 'Received', color: 'bg-blue-100 text-blue-700' },
  under_review: { label: 'Under Review', color: 'bg-amber-100 text-amber-700' },
  accepted: { label: 'Accepted', color: 'bg-emerald-100 text-emerald-700' },
  rejected: { label: 'Not Selected', color: 'bg-gray-100 text-gray-700' },
  withdrawn: { label: 'Withdrawn', color: 'bg-red-100 text-red-700' },
};

// Format currency
const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export default function ProjectProcurement() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Dark mode state (synced with localStorage)
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('keyMadeDarkMode');
    return saved === 'true';
  });
  
  // Check if coming from estimate page with selected items
  const fromEstimate = searchParams.get('from') === 'estimate';
  const estimateId = searchParams.get('estimateId');
  const selectedItemIds = searchParams.get('items')?.split(',').filter(Boolean) || [];
  
  // State
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'rfps');
  const [project, setProject] = useState(null);
  const [estimates, setEstimates] = useState([]);
  const [rfps, setRfps] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialog states
  const [showCreateRFP, setShowCreateRFP] = useState(false);
  const [showRFPDetail, setShowRFPDetail] = useState(null);
  const [showBidReview, setShowBidReview] = useState(null);
  const [showAddBid, setShowAddBid] = useState(null);
  
  // Pre-selected items state (from estimate page)
  const [preSelectedItems, setPreSelectedItems] = useState(selectedItemIds);
  const [preSelectedEstimateId, setPreSelectedEstimateId] = useState(estimateId);

  // Auto-open create RFP dialog if coming from estimate page
  useEffect(() => {
    if (fromEstimate && selectedItemIds.length > 0) {
      setShowCreateRFP(true);
    }
  }, [fromEstimate]);

  // Listen for Quick Actions events
  useEffect(() => {
    const handleQuickAction = (e) => {
      const { action } = e.detail || {};
      if (action === 'new-rfp' || action === 'rfp') {
        setShowCreateRFP(true);
      } else if (action === 'compare') {
        // Show comparison view if we have bids
        setActiveTab('allbids');
        toast.info('Showing all bids for comparison');
      }
    };
    
    const handleCompareBids = () => {
      setActiveTab('allbids');
    };
    
    window.addEventListener('quickAction', handleQuickAction);
    window.addEventListener('compareBids', handleCompareBids);
    
    return () => {
      window.removeEventListener('quickAction', handleQuickAction);
      window.removeEventListener('compareBids', handleCompareBids);
    };
  }, []);

  // Fetch data
  useEffect(() => {
    if (projectId) {
      fetchData();
    }
  }, [projectId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [projectRes, estimatesRes, rfpsRes, contractorsRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}`),
        axios.get(`${API}/projects/${projectId}/estimates`),
        axios.get(`${API}/projects/${projectId}/contractor-rfps`),
        axios.get(`${API}/contractors`).catch(() => ({ data: { contractors: [] } })),
      ]);
      
      setProject(projectRes.data.project || projectRes.data);
      setEstimates(estimatesRes.data.estimates || []);
      setRfps(rfpsRes.data.contractor_rfps || []);
      setContractors(contractorsRes.data.contractors || contractorsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load procurement data');
    } finally {
      setLoading(false);
    }
  };

  // Summary stats
  const stats = useMemo(() => ({
    totalRFPs: rfps.length,
    draftRFPs: rfps.filter(r => r.status === 'draft').length,
    awaitingBids: rfps.filter(r => r.status === 'sent').length,
    bidsReceived: rfps.filter(r => r.status === 'responses_received').length,
    awarded: rfps.filter(r => r.status === 'awarded').length,
    totalAwarded: rfps.filter(r => r.status === 'awarded').reduce((sum, r) => sum + (r.awarded_amount || 0), 0),
  }), [rfps]);

  if (loading) {
    return (
      <div className={`flex items-center justify-center min-h-screen ${darkMode ? 'bg-[#0D0D0D]' : 'bg-[#F5F3EF]'}`}>
        <Loader2 className="w-8 h-8 animate-spin text-[#70170F]" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-[#0D0D0D]' : 'bg-[#F5F3EF]'}`}>
      {/* Header */}
      <div className={`sticky top-0 z-10 border-b ${darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : 'bg-white border-[#E5E2DD]'}`}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          {/* Breadcrumb Navigation */}
          <div className={`flex items-center gap-2 text-sm mb-2 ${darkMode ? 'text-[#8A8A8A]' : 'text-[#6B6965]'}`}>
            <button 
              onClick={() => navigate('/dashboard')} 
              className={`hover:underline ${darkMode ? 'hover:text-[#E5E2DD]' : 'hover:text-[#1A1918]'}`}
            >
              My Desk
            </button>
            <ChevronRight className="w-4 h-4" />
            <button 
              onClick={() => navigate('/projects')} 
              className={`hover:underline ${darkMode ? 'hover:text-[#E5E2DD]' : 'hover:text-[#1A1918]'}`}
            >
              Projects
            </button>
            <ChevronRight className="w-4 h-4" />
            <button 
              onClick={() => navigate(`/projects/${projectId}/hub`)} 
              className={`hover:underline ${darkMode ? 'hover:text-[#E5E2DD]' : 'hover:text-[#1A1918]'}`}
            >
              {project?.project_name || project?.name || 'Project'}
            </button>
            <ChevronRight className="w-4 h-4" />
            <span className={`font-medium ${darkMode ? 'text-[#E5E2DD]' : 'text-[#1A1918]'}`}>Contractor RFPs & Bids</span>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(`/projects/${projectId}/hub`)} className={darkMode ? 'text-[#E5E2DD] hover:bg-[#1A1A1A]' : ''}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className={`text-2xl font-heading font-bold ${darkMode ? 'text-[#E5E2DD]' : 'text-[#1A1918]'}`}>
                    Contractor RFPs & Bids
                  </h1>
                  <HelpIcon elementId="bid-request" position="inline" />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                onClick={() => setShowCreateRFP(true)} 
                className="bg-[#70170F] hover:bg-[#8B1D12] text-white"
                disabled={estimates.length === 0}
              >
                <Plus className="w-4 h-4 mr-2" />
                Generate RFP
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <Card className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#70170F]" />
                <div>
                  <p className={`text-2xl font-bold ${darkMode ? 'text-[#E5E2DD]' : ''}`}>{stats.totalRFPs}</p>
                  <p className={`text-xs ${darkMode ? 'text-[#8A8A8A]' : 'text-muted-foreground'}`}>Total RFPs</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Send className="w-5 h-5 text-blue-600" />
                <div>
                  <p className={`text-2xl font-bold ${darkMode ? 'text-[#E5E2DD]' : ''}`}>{stats.awaitingBids}</p>
                  <p className="text-xs text-muted-foreground">Awaiting Bids</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-amber-600" />
                <div>
                  <p className={`text-2xl font-bold ${darkMode ? 'text-[#E5E2DD]' : ''}`}>{stats.bidsReceived}</p>
                  <p className={`text-xs ${darkMode ? 'text-[#8A8A8A]' : 'text-muted-foreground'}`}>Bids Received</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5 text-emerald-600" />
                <div>
                  <p className={`text-2xl font-bold ${darkMode ? 'text-[#E5E2DD]' : ''}`}>{stats.awarded}</p>
                  <p className={`text-xs ${darkMode ? 'text-[#8A8A8A]' : 'text-muted-foreground'}`}>Awarded</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={`col-span-2 ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-emerald-600" />
                <div>
                  <p className={`text-2xl font-bold ${darkMode ? 'text-[#E5E2DD]' : ''}`}>{formatCurrency(stats.totalAwarded)}</p>
                  <p className={`text-xs ${darkMode ? 'text-[#8A8A8A]' : 'text-muted-foreground'}`}>Total Awarded Value</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* No Estimates Warning */}
        {estimates.length === 0 && (
          <Card className={`mb-6 ${darkMode ? 'bg-amber-900/20 border-amber-700/50' : 'border-amber-200 bg-amber-50'}`}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600" />
                <div>
                  <p className={`font-medium ${darkMode ? 'text-amber-400' : 'text-amber-800'}`}>No Estimates Found</p>
                  <p className={`text-sm ${darkMode ? 'text-amber-500' : 'text-amber-700'}`}>
                    Create an estimate first to generate an RFP. RFPs are built from your estimate scope.
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  className={`ml-auto ${darkMode ? 'border-amber-600 text-amber-400 hover:bg-amber-900/30' : 'border-amber-300 text-amber-700 hover:bg-amber-100'}`}
                  onClick={() => navigate(`/projects/${projectId}/estimates/new`)}
                >
                  Create Estimate
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex items-center justify-between mb-4">
            <TabsList className={darkMode ? 'bg-[#1A1A1A]' : ''}>
              <TabsTrigger value="rfps" className={`gap-2 ${darkMode ? 'data-[state=active]:bg-[#2A2A2A] data-[state=active]:text-[#E5E2DD]' : ''}`}>
                <FileText className="w-4 h-4" />
                RFPs ({rfps.length})
              </TabsTrigger>
              <TabsTrigger value="bids" className={`gap-2 ${darkMode ? 'data-[state=active]:bg-[#2A2A2A] data-[state=active]:text-[#E5E2DD]' : ''}`}>
                <Receipt className="w-4 h-4" />
                All Bids
              </TabsTrigger>
              <TabsTrigger value="contractors" className={`gap-2 ${darkMode ? 'data-[state=active]:bg-[#2A2A2A] data-[state=active]:text-[#E5E2DD]' : ''}`}>
                <HardHat className="w-4 h-4" />
                Contractors
              </TabsTrigger>
            </TabsList>

            <div className="relative">
              <Search className={`w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 ${darkMode ? 'text-[#8A8A8A]' : 'text-muted-foreground'}`} />
              <Input
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`pl-9 w-64 ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A] text-[#E5E2DD]' : ''}`}
              />
            </div>
          </div>

          {/* RFPs Tab */}
          <TabsContent value="rfps">
            <div className="space-y-4">
              {rfps.length === 0 ? (
                <Card className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
                  <CardContent className="py-12 text-center">
                    <FileText className={`w-12 h-12 mx-auto mb-4 ${darkMode ? 'text-[#4A4A4A]' : 'text-muted-foreground'}`} />
                    <h3 className={`text-lg font-medium mb-2 ${darkMode ? 'text-[#E5E2DD]' : ''}`}>No RFPs Yet</h3>
                    <p className={`mb-4 ${darkMode ? 'text-[#8A8A8A]' : 'text-muted-foreground'}`}>
                      Generate an RFP from your estimate to start collecting contractor bids.
                    </p>
                    <Button 
                      onClick={() => setShowCreateRFP(true)}
                      disabled={estimates.length === 0}
                      className="bg-[#70170F] hover:bg-[#8B1D12] text-white"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Generate First RFP
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                rfps.map((rfp) => (
                  <RFPCard 
                    key={rfp.id} 
                    rfp={rfp} 
                    onView={() => setShowRFPDetail(rfp)}
                    onAddBid={() => setShowAddBid(rfp)}
                    onRefresh={fetchData}
                    darkMode={darkMode}
                  />
                ))
              )}
            </div>
          </TabsContent>

          {/* All Bids Tab */}
          <TabsContent value="bids">
            <AllBidsView rfps={rfps} onViewBid={setShowBidReview} darkMode={darkMode} />
          </TabsContent>

          {/* Contractors Tab */}
          <TabsContent value="contractors">
            <ContractorsView contractors={contractors} rfps={rfps} darkMode={darkMode} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Create RFP Dialog */}
      <CreateRFPDialog 
        open={showCreateRFP}
        onClose={() => setShowCreateRFP(false)}
        projectId={projectId}
        estimates={estimates}
        contractors={contractors}
        onSuccess={() => {
          setShowCreateRFP(false);
          fetchData();
        }}
        onViewRFP={(rfp) => setShowRFPDetail(rfp)}
        darkMode={darkMode}
      />

      {/* RFP Detail Sheet */}
      {showRFPDetail && (
        <RFPDetailSheet 
          rfp={showRFPDetail}
          onClose={() => setShowRFPDetail(null)}
          onAddBid={() => {
            setShowAddBid(showRFPDetail);
            setShowRFPDetail(null);
          }}
          onRefresh={fetchData}
          contractors={contractors}
          darkMode={darkMode}
        />
      )}

      {/* Add Bid Dialog */}
      {showAddBid && (
        <AddBidDialog
          rfp={showAddBid}
          onClose={() => setShowAddBid(null)}
          onSuccess={() => {
            setShowAddBid(null);
            fetchData();
          }}
        />
      )}

      {/* Bid Review Sheet - Split View */}
      {showBidReview && (
        <BidReviewSheet
          bid={showBidReview}
          onClose={() => setShowBidReview(null)}
          onRefresh={fetchData}
        />
      )}
    </div>
  );
}

// RFP Card Component
function RFPCard({ rfp, onView, onAddBid, onRefresh, darkMode }) {
  const statusConfig = RFP_STATUS[rfp.status] || RFP_STATUS.draft;
  const StatusIcon = statusConfig.icon;
  const [bids, setBids] = useState([]);
  const [sendDialog, setSendDialog] = useState({ open: false, loading: false });
  const [warningContacts, setWarningContacts] = useState([]);
  const [sendResult, setSendResult] = useState(null);

  useEffect(() => {
    axios.get(`${API}/contractor-rfps/${rfp.id}/bids`)
      .then(res => setBids(res.data.bids || []))
      .catch(() => setBids([]));
  }, [rfp.id]);

  // Recipients currently pending — those are who /send will email by default.
  const pendingRecipients = (rfp.contractors || []).filter(c => c.status === 'pending');
  const skippedNoEmail = pendingRecipients.filter(c => !c.email);
  const willSendTo = pendingRecipients.filter(c => !!c.email);

  // Open the confirm dialog, and probe each pending recipient's
  // relationship_status. Warning-flagged contacts surface inside the confirm
  // even if they slipped past the upstream warning gate.
  const openSendDialog = async () => {
    setSendResult(null);
    const ids = willSendTo.map(c => c.contractor_id).filter(Boolean);
    let warnings = [];
    if (ids.length > 0) {
      // Cheap parallel fetch — each contact GET is small.
      try {
        const results = await Promise.allSettled(
          ids.map(id => axios.get(`${API}/contacts/${id}`)),
        );
        warnings = results
          .filter(r => r.status === 'fulfilled' && r.value.data?.relationship_status === 'warning')
          .map(r => r.value.data);
      } catch { /* swallow — warnings are advisory */ }
    }
    setWarningContacts(warnings);
    setSendDialog({ open: true, loading: false });
  };

  const handleConfirmSend = async () => {
    setSendDialog(s => ({ ...s, loading: true }));
    try {
      const res = await axios.post(`${API}/contractor-rfps/${rfp.id}/send`, {});
      const { sent, failed, skipped_no_email } = res.data || {};
      setSendResult(res.data);
      if (sent > 0) {
        toast.success(
          `RFP sent to ${sent} recipient${sent === 1 ? '' : 's'}` +
          ((failed?.length || 0) > 0 ? ` · ${failed.length} failed` : '') +
          ((skipped_no_email?.length || 0) > 0 ? ` · ${skipped_no_email.length} had no email` : ''),
        );
      } else if ((failed?.length || 0) > 0) {
        toast.error(`Send failed for all ${failed.length} recipient(s) — none marked as sent`);
      } else {
        toast(`No recipients to send — ${skipped_no_email?.length || 0} had no email`);
      }
      onRefresh();
      // Auto-close only on full success so the user can see failures.
      if ((failed?.length || 0) === 0) {
        setSendDialog({ open: false, loading: false });
      } else {
        setSendDialog(s => ({ ...s, loading: false }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send RFP');
      setSendDialog(s => ({ ...s, loading: false }));
    }
  };

  return (
    <Card className={`hover:shadow-md transition-shadow ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          {/* RFP Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={`font-mono text-sm ${darkMode ? 'text-[#8A8A8A]' : 'text-muted-foreground'}`}>{rfp.rfp_number}</span>
              <Badge className={statusConfig.color}>
                <StatusIcon className="w-3 h-3 mr-1" />
                {statusConfig.label}
              </Badge>
              {rfp.rfp_type === 'specialty' && (
                <Badge variant="outline" className={darkMode ? 'border-[#2A2A2A] text-[#E5E2DD]' : ''}>{rfp.trade}</Badge>
              )}
            </div>
            <h3 className={`font-medium text-lg mb-1 ${darkMode ? 'text-[#E5E2DD]' : ''}`}>{rfp.title}</h3>
            <div className={`flex items-center gap-4 text-sm ${darkMode ? 'text-[#8A8A8A]' : 'text-muted-foreground'}`}>
              <span className="flex items-center gap-1">
                <ClipboardList className="w-4 h-4" />
                {rfp.scope_items?.length || 0} scope items
              </span>
              <span className="flex items-center gap-1">
                <HardHat className="w-4 h-4" />
                {rfp.contractors?.length || 0} contractors
              </span>
              <span className="flex items-center gap-1">
                <Receipt className="w-4 h-4" />
                {bids.length} bids
              </span>
              {rfp.bid_due_date && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Due: {formatDate(rfp.bid_due_date)}
                </span>
              )}
            </div>
          </div>

          {/* Bid Summary */}
          {bids.length > 0 && (
            <div className="text-right">
              <p className={`text-xs ${darkMode ? 'text-[#8A8A8A]' : 'text-muted-foreground'}`}>Lowest Bid</p>
              <p className="text-lg font-bold text-emerald-600">
                {formatCurrency(Math.min(...bids.map(b => b.total_bid || Infinity)))}
              </p>
              <p className={`text-xs ${darkMode ? 'text-[#8A8A8A]' : 'text-muted-foreground'}`}>
                Range: {formatCurrency(Math.min(...bids.map(b => b.total_bid || 0)))} - {formatCurrency(Math.max(...bids.map(b => b.total_bid || 0)))}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2">
            {(rfp.status === 'draft' || pendingRecipients.length > 0) && (
              <Button
                variant="outline"
                size="sm"
                onClick={openSendDialog}
                disabled={pendingRecipients.length === 0}
                title={pendingRecipients.length === 0 ? 'No pending recipients to send to' : ''}
                data-testid={`rfp-send-btn-${rfp.id}`}
                className={darkMode ? 'border-[#2A2A2A] text-[#E5E2DD] hover:bg-[#2A2A2A]' : ''}
              >
                <Send className="w-4 h-4 mr-1" />
                Send {pendingRecipients.length > 0 ? `(${pendingRecipients.length})` : ''}
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={onAddBid} className={darkMode ? 'border-[#2A2A2A] text-[#E5E2DD] hover:bg-[#2A2A2A]' : ''}>
              <FileUp className="w-4 h-4 mr-1" />
              Add Bid
            </Button>
            <Button variant="ghost" size="icon" onClick={onView} className={darkMode ? 'text-[#E5E2DD] hover:bg-[#2A2A2A]' : ''}>
              <Eye className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Send-RFP confirm dialog */}
        <Dialog open={sendDialog.open} onOpenChange={(o) => !sendDialog.loading && setSendDialog({ open: o, loading: false })}>
          <DialogContent className="max-w-md" data-testid={`rfp-send-confirm-dialog-${rfp.id}`}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Send className="w-5 h-5 text-primary" />
                Send RFP to {willSendTo.length} contractor{willSendTo.length === 1 ? '' : 's'}?
              </DialogTitle>
              <DialogDescription>
                This will email the RFP from your verified KEY-MADE sender. Status flips to <strong>sent</strong> only for recipients whose email actually goes out.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 py-2">
              {/* Warning gate at send time — surfaces any warning-flagged
                  recipients that slipped past upstream selection. */}
              {warningContacts.length > 0 && (
                <div className="border border-red-200 bg-red-50 rounded-md p-3 space-y-1.5">
                  <div className="flex items-center gap-1.5 text-sm font-medium text-red-800">
                    <AlertCircle className="w-4 h-4" />
                    {warningContacts.length} warning-flagged recipient{warningContacts.length === 1 ? '' : 's'}
                  </div>
                  <ul className="text-xs text-red-900 ml-5 list-disc">
                    {warningContacts.map(c => (
                      <li key={c.id} data-testid={`rfp-send-warning-${c.id}`}>
                        <span className="font-medium">{(c.first_name || '') + ' ' + (c.last_name || '')}</span>
                        {c.relationship_status_reason && (
                          <span className="italic"> — {c.relationship_status_reason}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Will-send list */}
              <div className="border rounded-md max-h-48 overflow-y-auto divide-y">
                {willSendTo.map(c => (
                  <div key={c.contractor_id} className="px-3 py-2 text-sm flex items-center justify-between gap-2">
                    <span className="truncate">
                      <span className="font-medium">{c.name || c.email}</span>
                      <span className="text-muted-foreground"> · {c.email}</span>
                    </span>
                  </div>
                ))}
                {willSendTo.length === 0 && (
                  <div className="px-3 py-3 text-xs text-muted-foreground">No pending recipients with an email.</div>
                )}
              </div>

              {/* Skipped (no email) heads-up */}
              {skippedNoEmail.length > 0 && (
                <p className="text-xs text-amber-700">
                  {skippedNoEmail.length} recipient{skippedNoEmail.length === 1 ? ' has' : 's have'} no email and will be skipped.
                </p>
              )}

              {/* Post-send result panel — only shown after a partial-failure send */}
              {sendResult && (sendResult.failed?.length > 0) && (
                <div className="border border-red-200 bg-red-50 rounded-md p-3">
                  <div className="text-sm font-medium text-red-800 mb-1.5">
                    {sendResult.failed.length} failed — not marked as sent
                  </div>
                  <ul className="text-xs text-red-900 ml-5 list-disc">
                    {sendResult.failed.map(f => (
                      <li key={f.contractor_id} data-testid={`rfp-send-failed-${f.contractor_id}`}>
                        {f.email} — {f.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setSendDialog({ open: false, loading: false })}
                disabled={sendDialog.loading}
              >
                {sendResult ? 'Close' : 'Cancel'}
              </Button>
              {!sendResult && (
                <Button
                  onClick={handleConfirmSend}
                  disabled={sendDialog.loading || willSendTo.length === 0}
                  data-testid={`rfp-send-confirm-${rfp.id}`}
                  className={warningContacts.length > 0 ? 'bg-red-600 hover:bg-red-700 text-white' : ''}
                >
                  {sendDialog.loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</>
                  ) : warningContacts.length > 0 ? (
                    `Send anyway (${willSendTo.length})`
                  ) : (
                    `Send (${willSendTo.length})`
                  )}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

// Create RFP Dialog
function CreateRFPDialog({ open, onClose, projectId, estimates, contractors, onSuccess, onViewRFP }) {
  const [selectedEstimate, setSelectedEstimate] = useState('');
  const [rfpType, setRfpType] = useState('general');
  const [trade, setTrade] = useState('');
  const [title, setTitle] = useState('');
  const [bidDueDate, setBidDueDate] = useState('');
  const [scopeSummary, setScopeSummary] = useState('');
  const [selectedContractors, setSelectedContractors] = useState([]);
  const [loading, setLoading] = useState(false);

  const trades = [
    'General Construction', 'Electrical', 'Plumbing', 'HVAC', 
    'Carpentry', 'Flooring', 'Painting', 'Roofing', 'Masonry',
    'Tile', 'Cabinetry', 'Countertops', 'Landscaping', 'Other'
  ];

  const handleCreate = async () => {
    if (!selectedEstimate) {
      toast.error('Please select an estimate');
      return;
    }

    try {
      setLoading(true);
      const res = await axios.post(`${API}/estimates/${selectedEstimate}/generate-rfp`, {
        title: title || `RFP for ${estimates.find(e => e.id === selectedEstimate)?.name || 'Project'}`,
        rfp_type: rfpType,
        trade: rfpType === 'specialty' ? trade : null,
        bid_due_date: bidDueDate || null,
        scope_summary: scopeSummary,
      });

      // Add contractors if selected
      if (selectedContractors.length > 0 && res.data.id) {
        for (const contractorId of selectedContractors) {
          await axios.post(`${API}/contractor-rfps/${res.data.id}/add-contractor?contractor_id=${contractorId}`);
        }
      }

      toast.success(`RFP ${res.data.rfp_number} created`);
      
      // Fetch the full RFP data and show it for review
      const rfpRes = await axios.get(`${API}/contractor-rfps/${res.data.id}`);
      onSuccess();
      onClose();
      
      // Open the RFP for review
      if (onViewRFP) {
        onViewRFP(rfpRes.data);
      }
    } catch (error) {
      console.error('Error creating RFP:', error);
      toast.error('Failed to create RFP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Generate Contractor RFP</DialogTitle>
          <DialogDescription>
            Create a Request for Proposal from your estimate to send to contractors
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Source Estimate */}
          <div className="space-y-2">
            <Label>Source Estimate *</Label>
            <Select value={selectedEstimate} onValueChange={setSelectedEstimate}>
              <SelectTrigger>
                <SelectValue placeholder="Select estimate to generate RFP from" />
              </SelectTrigger>
              <SelectContent>
                {estimates.map(est => (
                  <SelectItem key={est.id} value={est.id}>
                    {est.name} - {formatCurrency(est.total_price || 0)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* RFP Type */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>RFP Type</Label>
              <Select value={rfpType} onValueChange={setRfpType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General (Full Project)</SelectItem>
                  <SelectItem value="specialty">Specialty (Specific Trade)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {rfpType === 'specialty' && (
              <div className="space-y-2">
                <Label>Trade</Label>
                <Select value={trade} onValueChange={setTrade}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select trade" />
                  </SelectTrigger>
                  <SelectContent>
                    {trades.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label>RFP Title</Label>
            <Input 
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Kitchen Renovation - General Construction"
            />
          </div>

          {/* Due Date */}
          <div className="space-y-2">
            <Label>Bid Due Date</Label>
            <Input 
              type="date"
              value={bidDueDate}
              onChange={(e) => setBidDueDate(e.target.value)}
            />
          </div>

          {/* Scope Summary */}
          <div className="space-y-2">
            <Label>Scope Summary</Label>
            <Textarea 
              value={scopeSummary}
              onChange={(e) => setScopeSummary(e.target.value)}
              placeholder="Brief description of the work scope..."
              rows={3}
            />
          </div>

          {/* Contractors to Invite */}
          {contractors.length > 0 && (
            <div className="space-y-2">
              <Label>Invite Contractors (Optional)</Label>
              <div className="border rounded-lg max-h-40 overflow-y-auto p-2 space-y-1">
                {contractors.map(c => (
                  <label 
                    key={c.id}
                    className="flex items-center gap-2 p-2 rounded hover:bg-muted cursor-pointer"
                  >
                    <input 
                      type="checkbox"
                      checked={selectedContractors.includes(c.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedContractors([...selectedContractors, c.id]);
                        } else {
                          setSelectedContractors(selectedContractors.filter(id => id !== c.id));
                        }
                      }}
                      className="rounded"
                    />
                    <HardHat className="w-4 h-4 text-muted-foreground" />
                    <span className="font-medium">{c.company_name || c.name}</span>
                    {c.trade && <Badge variant="outline" className="text-xs">{c.trade}</Badge>}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            onClick={handleCreate} 
            disabled={loading || !selectedEstimate}
            className="bg-[#70170F] hover:bg-[#8B1D12]"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
            Generate RFP
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// RFP Detail Sheet
function RFPDetailSheet({ rfp, onClose, onAddBid, onRefresh, contractors }) {
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    title: rfp.title || '',
    scope_summary: rfp.scope_summary || '',
    bid_due_date: rfp.bid_due_date || '',
  });
  const [showAddContractor, setShowAddContractor] = useState(false);
  const [selectedNewContractors, setSelectedNewContractors] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchBids();
    // Reset edit form when RFP changes
    setEditForm({
      title: rfp.title || '',
      scope_summary: rfp.scope_summary || '',
      bid_due_date: rfp.bid_due_date || '',
    });
  }, [rfp.id, rfp.title, rfp.scope_summary, rfp.bid_due_date]);

  const fetchBids = async () => {
    try {
      const res = await axios.get(`${API}/contractor-rfps/${rfp.id}/bids`);
      setBids(res.data.bids || []);
    } catch (error) {
      console.error('Error fetching bids:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    try {
      await axios.put(`${API}/contractor-rfps/${rfp.id}`, editForm);
      toast.success('RFP updated');
      setIsEditing(false);
      onRefresh();
    } catch (error) {
      console.error('Error updating RFP:', error);
      toast.error('Failed to update RFP');
    }
  };

  const handleAddContractors = async () => {
    if (selectedNewContractors.length === 0) return;
    
    try {
      for (const contractorId of selectedNewContractors) {
        await axios.post(`${API}/contractor-rfps/${rfp.id}/add-contractor?contractor_id=${contractorId}`);
      }
      toast.success(`Added ${selectedNewContractors.length} contractor(s) to RFP`);
      setSelectedNewContractors([]);
      setShowAddContractor(false);
      onRefresh();
    } catch (error) {
      console.error('Error adding contractors:', error);
      toast.error('Failed to add contractors');
    }
  };

  const handleSendToContractors = async () => {
    if (!rfp.contractors?.length) {
      toast.error('Please add contractors to this RFP first');
      return;
    }
    const pendingList = (rfp.contractors || []).filter(c => c.status === 'pending' && c.email);
    if (pendingList.length === 0) {
      toast.error('No pending recipients with an email to send to');
      return;
    }
    const summary = pendingList.map(c => `• ${c.name || c.email} (${c.email})`).join('\n');
    if (!window.confirm(`Send RFP to ${pendingList.length} contractor(s)?\n\n${summary}\n\nReal email will be dispatched. Status flips to "sent" only for successful deliveries.`)) {
      return;
    }
    try {
      setSending(true);
      const res = await axios.post(`${API}/contractor-rfps/${rfp.id}/send`, {});
      const { sent, failed, skipped_no_email } = res.data || {};
      if (sent > 0) {
        toast.success(
          `RFP sent to ${sent} recipient${sent === 1 ? '' : 's'}` +
          ((failed?.length || 0) > 0 ? ` · ${failed.length} failed (not marked sent)` : '') +
          ((skipped_no_email?.length || 0) > 0 ? ` · ${skipped_no_email.length} had no email` : ''),
        );
      } else {
        toast.error(`No emails went out — ${failed?.length || 0} failed, ${skipped_no_email?.length || 0} had no email`);
      }
      onRefresh();
    } catch (error) {
      console.error('Error sending RFP:', error);
      toast.error(error.response?.data?.detail || 'Failed to send RFP');
    } finally {
      setSending(false);
    }
  };

  const handleRemoveContractor = async (contractorId) => {
    if (!window.confirm('Remove this contractor from the RFP?')) return;
    
    try {
      await axios.delete(`${API}/contractor-rfps/${rfp.id}/contractor/${contractorId}`);
      toast.success('Contractor removed');
      onRefresh();
    } catch (error) {
      console.error('Error removing contractor:', error);
      toast.error('Failed to remove contractor');
    }
  };

  const handleAward = async (bidId) => {
    if (!window.confirm('Award this contract? Other bids will be marked as not selected.')) return;
    
    try {
      await axios.post(`${API}/contractor-rfps/${rfp.id}/award/${bidId}`);
      toast.success('Contract awarded');
      onRefresh();
      onClose();
    } catch (error) {
      toast.error('Failed to award contract');
    }
  };

  // Filter out contractors already added to the RFP
  const availableContractors = contractors?.filter(c => 
    !rfp.contractors?.some(rc => rc.contractor_id === c.id)
  ) || [];

  return (
    <Sheet open={true} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground font-mono">{rfp.rfp_number}</p>
              {isEditing ? (
                <Input 
                  value={editForm.title}
                  onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                  className="mt-1 font-semibold"
                  placeholder="RFP Title"
                />
              ) : (
                <SheetTitle>{rfp.title}</SheetTitle>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Badge className={RFP_STATUS[rfp.status]?.color}>
                {RFP_STATUS[rfp.status]?.label}
              </Badge>
              {rfp.status === 'draft' && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => isEditing ? handleSaveEdit() : setIsEditing(true)}
                >
                  {isEditing ? <Check className="w-4 h-4" /> : <Edit2 className="w-4 h-4" />}
                </Button>
              )}
            </div>
          </div>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Action Buttons */}
          {rfp.status === 'draft' && (
            <div className="flex gap-2">
              <Button 
                onClick={handleSendToContractors}
                disabled={!rfp.contractors?.length || sending}
                className="bg-emerald-600 hover:bg-emerald-700 text-white flex-1"
              >
                {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Send to Contractors
              </Button>
            </div>
          )}
          
          {/* Scope Summary */}
          <div>
            <h4 className="font-medium mb-2">Scope Summary</h4>
            {isEditing ? (
              <Textarea
                value={editForm.scope_summary}
                onChange={(e) => setEditForm(prev => ({ ...prev, scope_summary: e.target.value }))}
                rows={3}
                placeholder="Describe the scope of work..."
              />
            ) : (
              <p className="text-sm text-muted-foreground">{rfp.scope_summary || 'No description provided'}</p>
            )}
          </div>

          {/* Bid Due Date */}
          <div>
            <h4 className="font-medium mb-2">Bid Due Date</h4>
            {isEditing ? (
              <Input 
                type="date"
                value={editForm.bid_due_date?.split('T')[0] || ''}
                onChange={(e) => setEditForm(prev => ({ ...prev, bid_due_date: e.target.value }))}
              />
            ) : (
              <p className="text-sm text-muted-foreground">
                {rfp.bid_due_date ? formatDate(rfp.bid_due_date) : 'Not set'}
              </p>
            )}
          </div>

          {/* Scope Items */}
          <div>
            <h4 className="font-medium mb-2">Scope Items ({rfp.scope_items?.length || 0})</h4>
            <div className="border rounded-lg divide-y max-h-60 overflow-y-auto">
              {rfp.scope_items?.map((item, idx) => (
                <div key={item.id || idx} className="p-3 text-sm">
                  <div className="flex justify-between">
                    <span className="font-medium">{item.description}</span>
                    <span className="text-muted-foreground">
                      {item.quantity} {item.unit}
                    </span>
                  </div>
                  {item.room && <p className="text-xs text-muted-foreground">{item.room}</p>}
                </div>
              ))}
            </div>
          </div>

          {/* Contractors */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium">Invited Contractors ({rfp.contractors?.length || 0})</h4>
              {rfp.status === 'draft' && (
                <Button variant="outline" size="sm" onClick={() => setShowAddContractor(true)}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Contractor
                </Button>
              )}
            </div>
            <div className="space-y-2">
              {rfp.contractors?.length === 0 ? (
                <div className="text-center py-4 border rounded-lg border-dashed">
                  <HardHat className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">No contractors invited yet</p>
                  <Button 
                    variant="link" 
                    size="sm" 
                    onClick={() => setShowAddContractor(true)}
                    className="text-[#70170F]"
                  >
                    Add contractors to this RFP
                  </Button>
                </div>
              ) : (
                rfp.contractors?.map((c, idx) => (
                  <div key={c.contractor_id || idx} className="flex items-center justify-between p-2 border rounded">
                    <div className="flex items-center gap-2">
                      <HardHat className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <span className="font-medium">{c.name}</span>
                        {c.email && <p className="text-xs text-muted-foreground">{c.email}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={`text-xs ${
                        c.status === 'responded' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                        c.status === 'sent' ? 'bg-blue-50 text-blue-700 border-blue-200' : ''
                      }`}>
                        {c.status === 'responded' ? 'Bid Received' : 
                         c.status === 'sent' ? 'RFP Sent' : 'Pending'}
                      </Badge>
                      {rfp.status === 'draft' && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
                          onClick={() => handleRemoveContractor(c.contractor_id)}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Bids */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium">Received Bids ({bids.length})</h4>
              <Button variant="outline" size="sm" onClick={onAddBid}>
                <FileUp className="w-4 h-4 mr-1" />
                Add Bid
              </Button>
            </div>
            
            {loading ? (
              <div className="flex justify-center py-4">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : bids.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No bids received yet
              </p>
            ) : (
              <div className="space-y-2">
                {bids.sort((a, b) => (a.total_bid || 0) - (b.total_bid || 0)).map((bid, idx) => (
                  <div 
                    key={bid.id}
                    className={`p-3 border rounded-lg ${
                      bid.status === 'accepted' ? 'border-emerald-300 bg-emerald-50' :
                      bid.status === 'rejected' ? 'opacity-60' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          {idx === 0 && bid.status !== 'rejected' && (
                            <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                          )}
                          <span className="font-medium">{bid.contractor_name}</span>
                          <Badge className={BID_STATUS[bid.status]?.color}>
                            {BID_STATUS[bid.status]?.label}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Submitted: {formatDate(bid.submitted_at)}
                          {bid.attachments?.length > 0 && ` • ${bid.attachments.length} documents`}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold">{formatCurrency(bid.total_bid)}</p>
                        {bid.status === 'received' && rfp.status !== 'awarded' && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="mt-1 text-emerald-600 border-emerald-300"
                            onClick={() => handleAward(bid.id)}
                          >
                            <Award className="w-3 h-3 mr-1" />
                            Award
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Portal Link */}
          {rfp.portal_access_code && (
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-sm font-medium text-blue-800 mb-1">Contractor Portal Link</p>
              <div className="flex items-center gap-2">
                <Input 
                  value={`${window.location.origin}/contractor-portal/${rfp.portal_access_code}`}
                  readOnly
                  className="text-xs bg-white"
                />
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/contractor-portal/${rfp.portal_access_code}`);
                    toast.success('Link copied');
                  }}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
        
        {/* Add Contractor Dialog */}
        <Dialog open={showAddContractor} onOpenChange={setShowAddContractor}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add Contractors to RFP</DialogTitle>
              <DialogDescription>
                Select contractors to invite for this RFP
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              {availableContractors.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  All available contractors have already been added to this RFP
                </p>
              ) : (
                <div className="border rounded-lg max-h-[300px] overflow-y-auto divide-y">
                  {availableContractors.map(c => (
                    <label 
                      key={c.id}
                      className="flex items-center gap-3 p-3 hover:bg-muted cursor-pointer"
                    >
                      <input 
                        type="checkbox"
                        checked={selectedNewContractors.includes(c.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedNewContractors(prev => [...prev, c.id]);
                          } else {
                            setSelectedNewContractors(prev => prev.filter(id => id !== c.id));
                          }
                        }}
                        className="rounded"
                      />
                      <div className="flex-1">
                        <div className="font-medium">{c.company_name}</div>
                        <div className="text-xs text-muted-foreground">
                          {c.specialty || 'General'} • {c.contact_name || 'No contact'}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddContractor(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleAddContractors}
                disabled={selectedNewContractors.length === 0}
                className="bg-[#70170F] hover:bg-[#8B1D12] text-white"
              >
                Add {selectedNewContractors.length || ''} Contractor{selectedNewContractors.length !== 1 ? 's' : ''}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </SheetContent>
    </Sheet>
  );
}

// Add Bid Dialog - For manually entering contractor bids (usually from PDF review)
function AddBidDialog({ rfp, onClose, onSuccess }) {
  const [contractorName, setContractorName] = useState('');
  const [contractorEmail, setContractorEmail] = useState('');
  const [contractorPhone, setContractorPhone] = useState('');
  const [totalBid, setTotalBid] = useState('');
  const [notes, setNotes] = useState('');
  const [exclusions, setExclusions] = useState('');
  const [proposedDuration, setProposedDuration] = useState('');
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleFileUpload = async (e) => {
    const newFiles = Array.from(e.target.files);
    setFiles([...files, ...newFiles.map(f => ({ name: f.name, file: f }))]);
  };

  const handleSubmit = async () => {
    if (!contractorName || !totalBid) {
      toast.error('Please enter contractor name and total bid');
      return;
    }

    try {
      setLoading(true);
      
      // Upload files first if any
      const attachments = [];
      for (const fileObj of files) {
        // In real implementation, upload to storage
        // For now, just track the file name
        attachments.push({ name: fileObj.name, url: '#' });
      }

      await axios.post(`${API}/contractor-rfps/${rfp.id}/bids`, {
        contractor_name: contractorName,
        contractor_email: contractorEmail,
        contractor_phone: contractorPhone,
        total_bid: parseFloat(totalBid.replace(/[^0-9.]/g, '')),
        notes,
        exclusions,
        proposed_duration: proposedDuration,
        attachments,
        submitted_via: 'manual'
      });

      toast.success('Bid recorded');
      onSuccess();
    } catch (error) {
      toast.error('Failed to record bid');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Record Contractor Bid</DialogTitle>
          <DialogDescription>
            Enter key information from the contractor&apos;s proposal. Attach their PDF for reference.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4 py-4">
          {/* Contractor Info */}
          <div className="space-y-2">
            <Label>Contractor Name *</Label>
            <Input 
              value={contractorName}
              onChange={(e) => setContractorName(e.target.value)}
              placeholder="Company name"
            />
          </div>
          <div className="space-y-2">
            <Label>Total Bid Amount *</Label>
            <Input 
              value={totalBid}
              onChange={(e) => setTotalBid(e.target.value)}
              placeholder="$0.00"
            />
          </div>
          <div className="space-y-2">
            <Label>Email</Label>
            <Input 
              value={contractorEmail}
              onChange={(e) => setContractorEmail(e.target.value)}
              placeholder="contractor@email.com"
            />
          </div>
          <div className="space-y-2">
            <Label>Phone</Label>
            <Input 
              value={contractorPhone}
              onChange={(e) => setContractorPhone(e.target.value)}
              placeholder="(555) 555-5555"
            />
          </div>
          <div className="space-y-2">
            <Label>Proposed Duration</Label>
            <Input 
              value={proposedDuration}
              onChange={(e) => setProposedDuration(e.target.value)}
              placeholder="e.g., 8 weeks"
            />
          </div>
          <div className="space-y-2">
            <Label>Exclusions</Label>
            <Input 
              value={exclusions}
              onChange={(e) => setExclusions(e.target.value)}
              placeholder="What's NOT included"
            />
          </div>
          
          {/* Notes */}
          <div className="col-span-2 space-y-2">
            <Label>Notes / Summary</Label>
            <Textarea 
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Key points from their proposal..."
              rows={3}
            />
          </div>

          {/* File Upload */}
          <div className="col-span-2 space-y-2">
            <Label>Attach Proposal Documents</Label>
            <div className="border-2 border-dashed rounded-lg p-4 text-center">
              <input 
                type="file" 
                multiple 
                accept=".pdf,.doc,.docx,.xls,.xlsx"
                onChange={handleFileUpload}
                className="hidden"
                id="bid-files"
              />
              <label htmlFor="bid-files" className="cursor-pointer">
                <FileUp className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">
                  Click to upload contractor proposal PDF
                </p>
              </label>
            </div>
            {files.length > 0 && (
              <div className="space-y-1">
                {files.map((f, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm p-2 bg-muted rounded">
                    <Paperclip className="w-4 h-4" />
                    <span className="flex-1">{f.name}</span>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-6 w-6"
                      onClick={() => setFiles(files.filter((_, i) => i !== idx))}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            onClick={handleSubmit}
            disabled={loading}
            className="bg-[#70170F] hover:bg-[#8B1D12]"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
            Record Bid
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// All Bids View - Shows all bids across all RFPs
function AllBidsView({ rfps, onViewBid, darkMode }) {
  const [allBids, setAllBids] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAllBids = async () => {
      const bidsPromises = rfps.map(rfp => 
        axios.get(`${API}/contractor-rfps/${rfp.id}/bids`)
          .then(res => (res.data.bids || []).map(b => ({ ...b, rfp })))
          .catch(() => [])
      );
      const results = await Promise.all(bidsPromises);
      setAllBids(results.flat());
      setLoading(false);
    };
    
    if (rfps.length > 0) {
      fetchAllBids();
    } else {
      // Use timeout to avoid sync setState in effect
      const timer = setTimeout(() => setLoading(false), 0);
      return () => clearTimeout(timer);
    }
  }, [rfps]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className={`w-8 h-8 animate-spin ${darkMode ? 'text-[#8A8A8A]' : ''}`} />
      </div>
    );
  }

  if (allBids.length === 0) {
    return (
      <Card className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
        <CardContent className="py-12 text-center">
          <Receipt className={`w-12 h-12 mx-auto mb-4 ${darkMode ? 'text-[#4A4A4A]' : 'text-muted-foreground'}`} />
          <h3 className={`text-lg font-medium mb-2 ${darkMode ? 'text-[#E5E2DD]' : ''}`}>No Bids Yet</h3>
          <p className={darkMode ? 'text-[#8A8A8A]' : 'text-muted-foreground'}>
            Contractor bids will appear here as they&apos;re received
          </p>
        </CardContent>
      </Card>
    );
  }

  // Group by status
  const accepted = allBids.filter(b => b.status === 'accepted');
  const received = allBids.filter(b => b.status === 'received' || b.status === 'under_review');
  const archived = allBids.filter(b => b.status === 'rejected' || b.status === 'withdrawn');

  return (
    <div className="space-y-6">
      {/* Accepted Bids */}
      {accepted.length > 0 && (
        <div>
          <h3 className={`font-medium mb-3 flex items-center gap-2 ${darkMode ? 'text-[#E5E2DD]' : ''}`}>
            <Award className="w-5 h-5 text-emerald-600" />
            Awarded Contracts ({accepted.length})
          </h3>
          <div className="space-y-2">
            {accepted.map(bid => (
              <BidRow key={bid.id} bid={bid} onClick={() => onViewBid(bid)} darkMode={darkMode} />
            ))}
          </div>
        </div>
      )}

      {/* Active Bids */}
      {received.length > 0 && (
        <div>
          <h3 className={`font-medium mb-3 flex items-center gap-2 ${darkMode ? 'text-[#E5E2DD]' : ''}`}>
            <Mail className="w-5 h-5 text-blue-600" />
            Active Bids ({received.length})
          </h3>
          <div className="space-y-2">
            {received.sort((a, b) => (a.total_bid || 0) - (b.total_bid || 0)).map(bid => (
              <BidRow key={bid.id} bid={bid} onClick={() => onViewBid(bid)} darkMode={darkMode} />
            ))}
          </div>
        </div>
      )}

      {/* Archived */}
      {archived.length > 0 && (
        <div>
          <h3 className={`font-medium mb-3 flex items-center gap-2 ${darkMode ? 'text-[#8A8A8A]' : 'text-muted-foreground'}`}>
            <Archive className="w-5 h-5" />
            Archived ({archived.length})
          </h3>
          <div className="space-y-2 opacity-60">
            {archived.map(bid => (
              <BidRow key={bid.id} bid={bid} onClick={() => onViewBid(bid)} darkMode={darkMode} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function BidRow({ bid, onClick, darkMode }) {
  return (
    <Card className={`cursor-pointer hover:shadow-md transition-shadow ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`} onClick={onClick}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span className={`font-medium ${darkMode ? 'text-[#E5E2DD]' : ''}`}>{bid.contractor_name}</span>
              <Badge className={BID_STATUS[bid.status]?.color}>
                {BID_STATUS[bid.status]?.label}
              </Badge>
            </div>
            <p className={`text-sm ${darkMode ? 'text-[#8A8A8A]' : 'text-muted-foreground'}`}>
              {bid.rfp?.title} • {formatDate(bid.submitted_at)}
            </p>
          </div>
          <div className="text-right">
            <p className={`text-xl font-bold ${darkMode ? 'text-[#E5E2DD]' : ''}`}>{formatCurrency(bid.total_bid)}</p>
            {bid.proposed_duration && (
              <p className={`text-xs ${darkMode ? 'text-[#8A8A8A]' : 'text-muted-foreground'}`}>{bid.proposed_duration}</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Contractors View
function ContractorsView({ contractors, rfps, darkMode }) {
  return (
    <div className="space-y-4">
      {contractors.length === 0 ? (
        <Card className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
          <CardContent className="py-12 text-center">
            <HardHat className={`w-12 h-12 mx-auto mb-4 ${darkMode ? 'text-[#4A4A4A]' : 'text-muted-foreground'}`} />
            <h3 className={`text-lg font-medium mb-2 ${darkMode ? 'text-[#E5E2DD]' : ''}`}>No Contractors Yet</h3>
            <p className={darkMode ? 'text-[#8A8A8A]' : 'text-muted-foreground'}>
              Contractors are automatically saved when you award a bid
            </p>
          </CardContent>
        </Card>
      ) : (
        contractors.map(contractor => (
          <Card key={contractor.id} className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${darkMode ? 'bg-[#70170F]/20' : 'bg-[#70170F]/10'}`}>
                    <HardHat className="w-5 h-5 text-[#70170F]" />
                  </div>
                  <div>
                    <p className={`font-medium ${darkMode ? 'text-[#E5E2DD]' : ''}`}>{contractor.company_name || contractor.name}</p>
                    <div className={`flex items-center gap-3 text-sm ${darkMode ? 'text-[#8A8A8A]' : 'text-muted-foreground'}`}>
                      {contractor.email && <span>{contractor.email}</span>}
                      {contractor.phone && <span>{contractor.phone}</span>}
                    </div>
                  </div>
                </div>
                {contractor.trade && (
                  <Badge variant="outline" className={darkMode ? 'border-[#2A2A2A] text-[#E5E2DD]' : ''}>{contractor.trade}</Badge>
                )}
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}

// Bid Review Sheet - Split view for PDF and data entry
function BidReviewSheet({ bid, onClose, onRefresh }) {
  const [showPdf, setShowPdf] = useState(true);
  
  return (
    <Sheet open={true} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-4xl">
        <SheetHeader>
          <div className="flex items-center justify-between">
            <SheetTitle>Review Bid: {bid.contractor_name}</SheetTitle>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowPdf(!showPdf)}
              >
                {showPdf ? <PanelLeftClose className="w-4 h-4 mr-1" /> : <PanelLeft className="w-4 h-4 mr-1" />}
                {showPdf ? 'Hide PDF' : 'Show PDF'}
              </Button>
            </div>
          </div>
        </SheetHeader>

        <div className="mt-4 flex gap-4 h-[calc(100vh-150px)]">
          {/* PDF Viewer */}
          {showPdf && bid.attachments?.length > 0 && (
            <div className="flex-1 border rounded-lg overflow-hidden bg-gray-100">
              <div className="h-full flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <FileText className="w-16 h-16 mx-auto mb-4" />
                  <p className="font-medium">PDF Preview</p>
                  <p className="text-sm">{bid.attachments[0]?.name}</p>
                  <Button variant="outline" size="sm" className="mt-2">
                    <Download className="w-4 h-4 mr-1" />
                    Download
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Bid Details */}
          <div className={`${showPdf && bid.attachments?.length > 0 ? 'w-96' : 'flex-1'} overflow-y-auto space-y-4`}>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Bid Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total Bid</span>
                  <span className="text-2xl font-bold">{formatCurrency(bid.total_bid)}</span>
                </div>
                {bid.proposed_duration && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Duration</span>
                    <span>{bid.proposed_duration}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Submitted</span>
                  <span>{formatDate(bid.submitted_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status</span>
                  <Badge className={BID_STATUS[bid.status]?.color}>
                    {BID_STATUS[bid.status]?.label}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {bid.notes && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Notes</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm">{bid.notes}</p>
                </CardContent>
              </Card>
            )}

            {bid.exclusions && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Exclusions</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-amber-700">{bid.exclusions}</p>
                </CardContent>
              </Card>
            )}

            {/* Contact */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Contact</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p className="font-medium">{bid.contractor_name}</p>
                {bid.contractor_email && (
                  <p className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    {bid.contractor_email}
                  </p>
                )}
                {bid.contractor_phone && (
                  <p className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    {bid.contractor_phone}
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
